<template>
  <div
    :class="{
      'card': true,
      'is-smallshadow': true,
      'complete': isFormComplete
    }">
    <div
      :class="{
        'card-body': true,
        'width-narrow': !readonly,
        'form-readonly': readonly
      }">
      <h2
        :class="{
          'heading-bigger mb-4': !readonly,
          'heading-small text-grey': readonly
        }">
        {{ $t('inquiryContactInfo.contactInformation') }}
      </h2>

      <template v-if="allDataLoaded">
        <div class="form-group">
          <label for="customer-name">{{ $t('user.firstName') }}</label>

          <template v-if="!readonly">
            <validation-provider
              name="customer-name"
              rules="required"
              v-slot="{ errors }">
              <f-input
                v-model="data.customer_name"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{ data.customer_name }}
          </template>
        </div>

        <div class="form-group">
          <label for="customer-surname">{{ $t('user.lastName') }}</label>

          <template v-if="!readonly">
            <validation-provider
              name="customer-surname"
              rules="required"
              v-slot="{ errors }">
              <f-input
                v-model="data.customer_surname"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{ data.customer_surname }}
          </template>
        </div>

        <div class="form-group">
          <label for="customer-email">{{ $t('user.emailAddress') }}</label>

          <template v-if="!readonly">
            <validation-provider
              rules="required|email"
              name="customer-email"
              v-slot="{ errors }">
              <f-input
                v-model="data.customer_email"
                type="email"
                :disabled="isCustomerInvited"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{ data.customer_email }}
          </template>
        </div>

        <div class="form-group">
          <label for="customer-phone">{{ $t('user.phoneNumber') }}</label>

          <template v-if="!readonly">
            <validation-provider
              name="customer-phone"
              rules="required|min:9|tel"
              mode="eager"
              v-slot="{ errors }">
              <vue-tel-input
                ref="tel-input"
                :key="defaultCountyCode"
                :defaultCountry="defaultCountyCode"
                :disabledFetchingCountry="true"
                :dynamicPlaceholder="false"
                :placeholder="''"
                :validCharactersOnly="true"
                :inputOptions="{
                  showDialCode:true
                }"
                :class="{
                  'f-input form-control': true,
                  'is-valid': !errors.length,
                  'is-invalid': errors.length
                }"
                v-model="data.customer_phone"
                :state="errors.length ? 'invalid' : null">
              </vue-tel-input>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{ data.customer_phone }}
          </template>
        </div>

        <div class="form-group">
          <label for="customer-city">{{ $t('user.city') }}</label>

          <template v-if="!readonly">
            <validation-provider
              name="customer-city"
              rules="required"
              v-slot="{ errors }">
              <f-input
                v-model="data.customer_city"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{ data.customer_city }}
          </template>
        </div>

        <div class="form-group">
          <label for="customer-postal-code">{{ $t('user.postalCode') }}</label>

          <template v-if="!readonly">
            <template v-if="formLanguageId === 2">
              <validation-provider
                rules="required|postal-code-gb"
                name="customer-postal-code"
                data-vv-validate-on="change|custom|blur|input"
                v-slot="{ errors }">
                <f-input
                  v-model="data.customer_post_code"
                  :state="errors.length ? 'invalid' : null"/>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </template>
            <template v-else>
              <validation-provider
                rules="required|postal-code"
                name="customer-postal-code"
                data-vv-validate-on="change|custom|blur|input"
                v-slot="{ errors }">
                <f-input
                  v-model="data.customer_post_code"
                  v-mask="'##-###'"
                  :state="errors.length ? 'invalid' : null"/>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </template>
          </template>
          <template v-else>
            {{ data.customer_post_code }}
          </template>
        </div>

        <div class="form-group">
          <label for="customer-contact-hours">{{ $t('inquiryContactInfo.preferredContactHours') }}</label>

          <template v-if="!readonly">
            <v-select
              name="customer-contact-hours"
              :options="contactHoursValues"
              v-model="data.customer_contact_hours"
              :reduce="(hours) => hours.value"
              :placeholder="$t('inquiryContactInfo.chooseHours')"
              label="label"/>
          </template>
          <template v-else>
            {{ getCustomerContactHoursLabel(data.customer_contact_hours) }}
          </template>
        </div>
      </template>
      <template v-else>
        <div class="loading-spinner-wrapper">
          <div class="loading-spinner pb-3 pt-3">
            <span class="loading-spinner-content">
              {{ $t('ui.loadingDataInProgress') }}&hellip;
            </span>
          </div>
        </div>
      </template>
    </div>

    <div
      v-if="!readonly"
      class="card-footer pt-3 pb-4 text-right">
      <f-button
        theme="primary"
        withArrow
        big
        @click.prevent="goToNextTab">
        {{ $t('ui.next') }}
      </f-button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';

export default {
  name: 'inquiry-contact-info-form',
  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    isDuplicate: {
      type: Boolean,
      default: false
    },
    isValid: {
      type: Boolean,
      default: false
    },
    customerID: {
      type: Number,
      default: 0
    },
    customerName: {
      type: String
    },
    customerSurname: {
      type: String
    },
    customerEmail: {
      type: String
    },
    customerPhone: {
      type: String
    },
    customerPostCode: {
      type: String
    },
    customerCity: {
      type: String
    },
    isCustomerInvited: {
      type: Boolean,
      default: false
    },
    customerContactHours: {
      type: [String, null],
      default: null
    },
    customerLanguageId: {
      type: Number,
      default: 1
    },
    userType: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters({
      langId: 'getUserLanguageId'
    }),
    formLanguageId () {
      if (this.userType === 'customer') {
        return this.langId;
      }

      return this.data.customer_language_id;
    },
    allDataLoaded () {
      return this.loaded && this.defaultCountyCode;
    },
    isFormComplete() {
      let isComplete = (this.loaded && !!this.data.customer_name && !!this.data.customer_surname && !!this.data.customer_email && !!this.data.customer_phone && !!this.data.customer_post_code && !!this.data.customer_city);

      isComplete = isComplete && this.isValid;
      if (this.loaded && (isComplete !== this.previousIsCompleteSet)) {
        Vue.set(this, 'previousIsCompleteSet', isComplete);
        this.$bus.$emit('inquiry-form-part-complete', this.index, isComplete, 'contact-info');
      }

      return isComplete;
    },
    isLanguageSet () {
      if (this.$store.getters['getUserHasLanguage']) {
        return true;
      }

      return false;
    },
    isLanguageError () {
      if (this.$store.getters['getUserLanguageHasError']) {
        return true;
      }

      return false;
    }
  },
  watch: {
    isLanguageError (newValue) {
      if (!newValue) {
        return;
      }
      Vue.nextTick(() => {
        Vue.set(this, 'defaultCountyCode', this.$store.getters['getUserPhoneCountryCode']);
      })
    },
    isLanguageSet (newValue) {
      if (!newValue) {
        return;
      }

      Vue.nextTick(() => {
        Vue.set(this, 'defaultCountyCode', this.$store.getters['getUserPhoneCountryCode']);
      })
    }
  },
  data() {
    return {
      data: {
        customer_name: '',
        customer_surname: '',
        customer_email: '',
        customer_phone: '',
        customer_post_code: '',
        customer_city: '',
        customer_contact_hours: null,
        customer_language_id: 0,
      },
      loaded: false,
      previousIsCompleteSet: false,
      contactHoursValues: [
        {
          value: 'MORNING',
          label: '8:00 - 12:00'
        },
        {
          value: 'NOON',
          label: '12:00 - 16:0'
        },
        {
          value: 'AFTERNOON',
          label: '16:00 - 20:00'
        }
      ],
      defaultCountyCode: null
    };
  },
  mounted () {
    if ((this.$store.getters['getUserHasLanguage'] || this.$store.getters['getUserLanguageHasError']) && !this.defaultCountyCode) {
      Vue.set(this, 'defaultCountyCode', this.$store.getters['getUserPhoneCountryCode']);
    }

    if (this.isEdit || this.isDuplicate) {
      Vue.nextTick(() => {
        Vue.set(this.data, 'customer_name', this.customerName);
        Vue.set(this.data, 'customer_surname', this.customerSurname);
        Vue.set(this.data, 'customer_email', this.customerEmail);
        Vue.set(this.data, 'customer_phone', this.customerPhone);
        Vue.set(this.data, 'customer_city', this.customerCity);
        Vue.set(this.data, 'customer_post_code', this.customerPostCode);
        Vue.set(this.data, 'customer_contact_hours', this.customerContactHours);
        Vue.set(this.data, 'customer_language_id', this.customerLanguageId);

        this.loaded = true;
      });
    } else {
      this.loaded = true;
    }
  },
  methods: {
    goToNextTab () {
      this.$bus.$emit('inquiry-form-go-next-tab', this.index + 1);
    },
    getData () {
      let telInput = this.$refs['tel-input'];

      return {
        customer_name: this.data.customer_name,
        customer_surname: this.data.customer_surname,
        customer_email: this.data.customer_email,
        customer_phone: telInput ? telInput.phoneObject.number.international : this.data.customer_phone,
        customer_post_code: this.data.customer_post_code,
        customer_city: this.data.customer_city,
        customer_contact_hours: this.data.customer_contact_hours
      };
    },
    setCustomerData (customer) {
      if (customer) {
        Vue.set(this.data, 'customer_name', customer.name);
        Vue.set(this.data, 'customer_surname', customer.surname);
        Vue.set(this.data, 'customer_email', customer.email);
        Vue.set(this.data, 'customer_phone', customer.phone);
        Vue.set(this.data, 'customer_city', customer.city);
        Vue.set(this.data, 'customer_post_code', customer.post_code);
        Vue.set(this.data, 'customer_contact_hours', null);
        Vue.set(this.data, 'customer_language_id', customer.language_id);
      } else {
        Vue.set(this.data, 'customer_name', '');
        Vue.set(this.data, 'customer_surname', '');
        Vue.set(this.data, 'customer_email', '');
        Vue.set(this.data, 'customer_phone', '');
        Vue.set(this.data, 'customer_city', '');
        Vue.set(this.data, 'customer_post_code', '');
        Vue.set(this.data, 'customer_contact_hours', null);
        Vue.set(this.data, 'customer_language_id', 1);
      }
    },
    getCustomerContactHoursLabel (value) {
      if (!value) {
        return '-';
      }

      let contactHours = this.contactHoursValues.find(item => item.value === value);
      if (!contactHours) {
        return '-';
      }

      return contactHours.label;
    }
  }
}
</script>
